/*
 * File: SimpleDialog.tsx
 * Description: Reusable simple dialog component
 **/

import {
    useLayoutEffect,
    useRef,
    useState,
    type ComponentPropsWithoutRef,
    type MouseEvent,
    type ReactNode,
    type RefObject,
    type SyntheticEvent,
} from "react";
import "./SimpleDialog.scss";

export interface SimpleDialogProps extends Omit<
    ComponentPropsWithoutRef<"dialog">,
    "children" | "className" | "onCancel" | "onClose" | "open"
> {
    children: ReactNode;
    header?: ReactNode;
    className?: string;
    isOpen?: boolean;
    initialFocusRef?: RefObject<HTMLElement | null>;
    onClose?: () => void;
    closeOnBackdropClick?: boolean;
}

export const SimpleDialog = ({
    children,
    header,
    className,
    isOpen,
    initialFocusRef,
    onClose,
    closeOnBackdropClick = true,
    onClick,
    ...dialogProps
}: SimpleDialogProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [isInternallyOpen, setIsInternallyOpen] = useState(true);
    const open = isOpen ?? isInternallyOpen;

    useLayoutEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (open && !dialog.open) {
            dialog.showModal();
            initialFocusRef?.current?.focus({ preventScroll: true });
        } else if (!open && dialog.open) {
            dialog.close();
        }
    }, [initialFocusRef, open]);

    const requestClose = () => {
        if (isOpen === undefined) {
            setIsInternallyOpen(false);
        }
        onClose?.();
    };

    const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
        event.preventDefault();
        requestClose();
    };

    const handleClick = (event: MouseEvent<HTMLDialogElement>) => {
        onClick?.(event);

        if (
            !event.defaultPrevented &&
            closeOnBackdropClick &&
            event.target === event.currentTarget
        ) {
            requestClose();
        }
    };

    return (
        <dialog
            {...dialogProps}
            ref={dialogRef}
            className={["simple-dialog", className].filter(Boolean).join(" ")}
            onCancel={handleCancel}
            onClick={handleClick}
        >
            <div className="simple-dialog__content">
                {header}
                {children}
            </div>
        </dialog>
    );
};
